import type { ICitaTechnologiasSalud } from '@/modules/Asistencial/interfaces'
import { calcularEdad } from '@/modules/Core/usings/generales'
import type { IConfPersonasPaciente } from '@/modules/Administracion/interfaces'
import type { IConfContratos } from '@/modules/Contratacion/interfaces'
import type { ICitaAdmision } from '@/modules/Admision/interfaces'
import { configuracionServicios } from '@/services'
import { mergePacienteSnapshot } from '@/modules/Core/usings/pacienteSnapshot'

export interface IHeaderPdfData extends Omit<IConfPersonasPaciente, 'eps'> {
  fechaAtencion?: ICitaTechnologiasSalud['cittAgendaFechaInicio']  | ICitaTechnologiasSalud['cittFechaInicio']
  horaAtencion?: ICitaTechnologiasSalud['cittHora']
  numAutorizacion?: ICitaTechnologiasSalud['cittNumeroAutorizacion']
  contrato?: IConfContratos
  technologia?: ICitaTechnologiasSalud | null
}

export interface IConfiguracionImpresion {
  "Nombre": boolean,
  "Identificación": boolean,
  "Fecha de nacimiento": boolean,
  "Edad": boolean,
  "Sexo": boolean,
  "Estado civil": boolean,
  "Ocupación": boolean,
  "Población": boolean,
  "Zona": boolean,
  "Ciudad": boolean,
  "Dirección": boolean,
  "Teléfono": boolean,
  "Autorización": boolean,
  "Fecha de atención": boolean,
  "Entidad de salud": boolean
}

async function getConfiguracionImpresion(): Promise<IConfiguracionImpresion> {
  try {
    const { data: { body: ImpresionCabeceraPdf } } = await configuracionServicios.list({
      cocoCategoria: 'Impresion',
      cocoCodigo: 'TBL_DATOSPACIENTE'
    })

    const jsonImpresionCabeceraPdf = ImpresionCabeceraPdf && ImpresionCabeceraPdf.length > 0
      ? JSON.parse(ImpresionCabeceraPdf[0]?.cocoValue || '{}')
      : null

    return jsonImpresionCabeceraPdf?.impresion || {
      "Nombre": true,
      "Identificación": true,
      "Fecha de nacimiento": true,
      "Edad": true,
      "Sexo": true,
      "Estado civil": true,
      "Ocupación": true,
      "Población": true,
      "Zona": true,
      "Ciudad": true,
      "Dirección": true,
      "Teléfono": true,
      "Autorización": true,
      "Fecha de atención": true,
      "Entidad de salud": true
    }
  } catch {
    // Configuración por defecto en caso de error
    return {
      "Nombre": true,
      "Identificación": true,
      "Fecha de nacimiento": true,
      "Edad": true,
      "Sexo": true,
      "Estado civil": true,
      "Ocupación": true,
      "Población": true,
      "Zona": true,
      "Ciudad": true,
      "Dirección": true,
      "Teléfono": true,
      "Autorización": true,
      "Fecha de atención": true,
      "Entidad de salud": true
    }
  }
}

/**
 * Genera la sección de datos del paciente para el PDF usando configuración dinámica.
 * Los campos mostrados se basan en la configuración almacenada en jsonImpresinCabeceraPdf.
 * @param data Datos del paciente y la atención (IHeaderPdfData)
 * @param admision Objeto de admisión (ICitaAdmision) - opcional
 * @returns Array con la estructura de la tarjeta del paciente para PDFMake
 */
export async function getHeaderInfo(
  data: IHeaderPdfData,
  admision?: ICitaAdmision,
  omitirFechaAtencion?: boolean
) {
  const configImpresion = await getConfiguracionImpresion()

  const paciente = mergePacienteSnapshot(data, data.technologia)
  if (!paciente) return [{ text: 'Datos del paciente no disponibles', style: 'tableHeader', margin: [0,0,0,10] }]
  const nombres = [
    paciente.copnNombre1,
    paciente.copnNombre2 || ''
  ].filter(Boolean).join(' ')
  const apellidos = [
    paciente.copnApellido1,
    paciente.copnApellido2 || ''
  ].filter(Boolean).join(' ')
  const tipoDoc = paciente.tipoidentificacion?.comaNombrecorto || ''
  const numDoc = paciente.copeIdentificacion || ''
  const fechaNac = paciente.coppFechanacimiento || ''
  const edad = fechaNac && fechaNac !== ''
    ? calcularEdad(fechaNac, admision?.ciadFechaOrigen?.split('T')[0])
    : ''
  const sexo = paciente.sexo?.comaNombrelargo || ''
  const direccion = (paciente.copeDireccion || '') + (paciente.coppTipoBarrio ? `, ${paciente.coppTipoBarrio}` : '')
  const ocupacion = paciente.ocupacion?.comaNombrelargo || ''
  const estadocivil = paciente.civil?.comaNombrecorto || ''
  const zona = paciente.zona?.comaNombrelargo || ''
  const poblacion = paciente.poblacion?.comaNombrecorto || ''
  const telefono = paciente.copeTelefono || ''
  const ciudad = paciente.divipola?.nomMunicipio || ''
  const departamento = paciente.divipola?.nomDepartamento || ''
  const eps = data.contrato?.empresa?.coemNombre || ''
  let fechaAtencion = data.fechaAtencion || ''
  if (fechaAtencion && fechaAtencion.includes('T'))
    fechaAtencion = fechaAtencion.split('T')[0]
  const horaAtencion = data.horaAtencion || ''
  const numAutorizacion = data.numAutorizacion || ''
  const regimen = data.contrato?.tiporegimen?.comaNombrelargo || '-'

  const crearCampo = (clave: string, label: string, valor: string) => {
    return configImpresion[clave]
      ? { text: `${label}: ${valor}\n`, style: 'textoCell' }
      : null
  }

  const crearCampoTitulo = (clave: string, label: string, valor: string) => {
    return configImpresion[clave]
      ? { text: [{ text: `${label}: `, style: 'textoCell' }, { text: `${valor}\n`, style: 'tituloCell' }] }
      : null
  }

  const camposPersonales = [
    crearCampoTitulo('Nombre', 'Nombre', `${nombres} ${apellidos}`),
    crearCampoTitulo('Identificación', 'Identificación', `${tipoDoc}. ${numDoc}`),
    crearCampo('Fecha de nacimiento', 'Fecha de nacimiento', fechaNac),
    crearCampo('Edad', 'Edad', edad),
    crearCampo('Sexo', 'Sexo', sexo),
    crearCampo('Estado civil', 'Estado civil', estadocivil),
    crearCampo('Ocupación', 'Ocupación', ocupacion)
  ].filter(Boolean)

  const camposUbicacion = [
    crearCampo('Población', 'Población', poblacion),
    crearCampo('Zona', 'Zona', zona),
    crearCampo('Ciudad', 'Ciudad', `${ciudad}-${departamento}`),
    crearCampo('Dirección', 'Dirección', direccion),
    crearCampo('Teléfono', 'Teléfono', telefono)
  ].filter(Boolean)

  // Generar campos de atención
  const camposAtencion = [
    crearCampo('Autorización', 'Autorización', numAutorizacion),
    omitirFechaAtencion ? null : crearCampo('Fecha de atención', 'Fecha de atención', `${fechaAtencion} ${horaAtencion}`),
    crearCampo('Entidad de salud', 'Entidad de salud', `${eps} - ${regimen}`)
  ].filter(Boolean)

  const tieneAtenciones = camposAtencion.length > 0

  const widths = tieneAtenciones ? ['70%', '30%'] : ['100%']

  const headers = tieneAtenciones
    ? [
        { text: 'DATOS DEL PACIENTE', style: 'tableHeaderLeft' },
        { text: 'DATOS DE LA ATENCIÓN', style: 'tableHeaderLeft' }
      ]
    : [{ text: 'DATOS DEL PACIENTE', style: 'tableHeaderLeft' }]

  const celdaDatosPersonales = {
    table: {
      widths: ['50%', '50%'],
      body: [
        [
          {
            text: camposPersonales.length > 0 ? camposPersonales : [{ text: 'Sin datos personales', style: 'textoCell' }],
            border: [false, false, false, false]
          },
          {
            text: camposUbicacion.length > 0 ? camposUbicacion : [{ text: '', style: 'textoCell' }],
            border: [false, false, false, false]
          }
        ]
      ]
    },
    layout: 'noBorders'
  }

  const celdaAtencion = tieneAtenciones ? {
    text: camposAtencion,
    border: [false, false, false, false]
  } : undefined

  // Generar fila de datos
  const filaDatos = tieneAtenciones
    ? [celdaDatosPersonales, celdaAtencion]
    : [celdaDatosPersonales]

  const cardModerna = {
    table: {
      headerRows: 1,
      widths: widths,
      body: [
        headers,
        filaDatos
      ],
    },
    layout: 'headerLineOnly'
  }

  return [cardModerna]
}
